import React, { useState } from 'react'
import { Icon, Menu, Popup } from 'semantic-ui-react'
import styled from 'styled-components'
import * as R from 'ramda'
import _ from 'lodash'

import useAxios, { mutate } from 'hooks/useAxios'
import userDomain, { ACCOUNTS_URL } from 'domains/user'
import useModal from 'hooks/useModal'
import toast from 'libs/toast'
import EditUserModalContent from 'organisms/EditUserModalContent'
import TeamMemberMappingModalContent from 'organisms/TeamMemberMappingModalContent'
import TransferOwnershipModalContent from 'organisms/TransferOwnershipModalContent'
import DeleteUserModalContent from 'organisms/DeleteUserModalContent'
import produce from 'immer'
import useSaveUserActivityLog, { ACTION_NAME } from 'hooks/useSaveUserActivityLog'
import { logMapTeamMember } from 'components/TeamMembers/utils'
import useUser from 'hooks/useUser'

/**
 *
 * @param {object} props
 * @param {import('components/UsersTable').User} props.user
 * @param {object[]} props.roleOptions
 * @param {object[]} props.surveyAccessSelectableRoles
 * @param {object[]} props.dashbardAccessesSelectableRoles
 */
export default function UserTableRowOptions({
  user,
  roleOptions,
  surveyAccessSelectableRoles,
  dashbardAccessesSelectableRoles,
  teamMembers,
  isTeamMembersLoading,
}) {
  const { data: currentUser } = useAxios({
    url: 'account/users/me/v2/',
  })
  const { fetch: updateAccountSurveyAccess } = useAxios({
    method: 'post',
    skip: true,
  })
  const { fetch: updateAccountDashboardAccess } = useAxios({
    method: 'post',
    skip: true,
  })
  const { fetch: resendInvitationEmail } = useAxios({
    method: 'post',
    url: 'account/invitation/resend/',
    skip: true,
  })
  const { loading: isEditUserLoading, fetch: editUser } = useAxios({
    method: 'patch',
    url: `account/accounts/${encodeURIComponent(user.email)}/`,
    skip: true,
  })
  const { loading: isEditTeamMemberMappingLoading, fetch: editTeamMemberMapping } = useAxios({
    method: 'post',
    url: `account/accounts/${user.id}/team-member-mapping/`,
    skip: true,
  })
  const { loading: isDeleteUserLoading, fetch: deleteUser } = useAxios({
    method: 'delete',
    url: `account/accounts/${user.id}/remove-from-workspace/`,
    skip: true,
  })

  const [isOptionsOpen, setIsOptionsOpen] = useState(false)
  const editUserModal = useModal()
  const teamMemberMappingModal = useModal()
  const transferOwnershipModal = useModal()
  const deleteUserModal = useModal()

  const saveUserActivityLog = useSaveUserActivityLog()
  const userInfo = useUser()
  const companyId = R.path(['selected_workspace', 'id'], userInfo)

  if (!currentUser) {
    return null
  }

  const hiddenOption = !userDomain.canSeeOptionsV2(currentUser, user)

  if (hiddenOption) {
    return null
  }

  function onResendInvitationEmail() {
    return resendInvitationEmail({
      data: {
        email: user.email,
      },
    })
      .then(() => {
        toast.success('An invitation email has been sent.')
      })
      .catch(() => {
        toast.error('Something went wrong, please try again.')
      })
  }

  function onEdit(
    role,
    accountId,
    disableSSO,
    loginMethods,
    isCustomLogin,
    surveys = [],
    dashboards = []
  ) {
    editUserModal.close()

    updateRole(role, disableSSO, loginMethods, isCustomLogin)
      .then(() => {
        mutate(ACCOUNTS_URL, (res) => _handleAfterUpdatedRoleSuccess(res))

        Promise.all([
          surveys.length > 0
            ? updateSurveyAccess(accountId, role, surveys).then(
                _handleAfterUpdateSurveyAccessSuccess
              )
            : Promise.resolve(),
          dashboards.length > 0 ? updateDashboardAccess(accountId, dashboards) : Promise.resolve(),
        ])
          .then(() => {
            toast.success('User updated.')

            saveUserActivityLog({
              action: ACTION_NAME.edit_user_role_and_survey_access,
              target: `acc_${accountId}`,
              requestBody: { surveys, role },
              statusCode: 200,
            })
          })
          .catch((error) => {
            toast.error('Oops! Something went wrong. Please try again.')
            saveUserActivityLog({
              action: ACTION_NAME.edit_user_role_and_survey_access,
              target: `acc_${accountId}`,
              requestBody: { surveys, role },
              statusCode: error.response.status,
            })
          })
      })
      .catch((error) => {
        const errorDetail = error?.response?.data?.detail
        toast.error(errorDetail || 'Oops! Something went wrong. Please try again.')
        saveUserActivityLog({
          action: ACTION_NAME.edit_user_role_and_survey_access,
          target: `acc_${accountId}`,
          requestBody: { surveys, role },
          statusCode: error?.response?.status ?? 500,
        })
      })

    function _handleAfterUpdatedRoleSuccess(accountsResponse) {
      const accountIndexToBeEdited = R.findIndex(
        (account) => account.id === accountId,
        accountsResponse.results
      )
      const newAccounts = {
        ...accountsResponse,
        results: R.adjust(
          accountIndexToBeEdited,
          (account) => {
            return {
              ...account,
              role: _.capitalize(role),
              disable_sso: disableSSO,
            }
          },
          accountsResponse.results
        ),
      }
      return newAccounts
    }

    function _handleAfterUpdateSurveyAccessSuccess() {
      const url = `account/accounts/${accountId}/control-survey-access/`
      mutate(url, true)
    }
  }

  function onEditTeamMemberMapping(teamMemberMapping) {
    return editTeamMemberMapping({
      data: {
        emails: teamMemberMapping,
      },
    })
      .then(() => {
        teamMemberMappingModal.close()
        toast.success('Team member mapping updated.')

        mutate(ACCOUNTS_URL, (accountsResponse) =>
          _mutateEditTeamMemberMappingCallback(accountsResponse, teamMemberMapping)
        )

        logMapTeamMember(saveUserActivityLog, companyId, user.email, teamMemberMapping, 200)
      })
      .catch((error) => {
        const emailErrors = error?.response?.data?.emails
        const hasInvalidEmail =
          emailErrors && Object.values(emailErrors).some((messages) => messages.length > 0)
        if (hasInvalidEmail) {
          const invalidIndex = Object.keys(emailErrors).find((key) => emailErrors[key].length > 0)
          const invalidEmail = teamMemberMapping[parseInt(invalidIndex, 10)]
          toast.error(
            `Please include an '@' in the email address. '${invalidEmail}' is missing an '@'.`
          )
        } else {
          toast.error('Oops! Something went wrong. Please try again.')
        }
        logMapTeamMember(
          saveUserActivityLog,
          companyId,
          user.email,
          teamMemberMapping,
          error?.response?.status ?? 500
        )
      })
  }

  function _mutateEditTeamMemberMappingCallback(accountsResponse, teamMemberMapping) {
    const accountIndexToBeEdited = R.findIndex(
      (account) => account.id === user.id,
      accountsResponse.results
    )
    return produce(accountsResponse, (draftAccountsResponse) => {
      draftAccountsResponse.results[accountIndexToBeEdited].team_member_mapping_emails =
        teamMemberMapping
    })
  }

  function updateRole(role, disableSSO, loginMethods, isCustomLogin) {
    return editUser({
      data: {
        role,
        disable_sso: disableSSO,
        login_methods: loginMethods,
        is_custom_login: isCustomLogin,
      },
    })
  }

  function updateSurveyAccess(accountId, role, surveys) {
    const url = `account/accounts/${accountId}/control-survey-access/`
    return updateAccountSurveyAccess({ url, data: { surveys, role } })
  }

  function updateDashboardAccess(accountId, dashboards) {
    const url = `account/accounts/${accountId}/control-dashboard-access/`
    return updateAccountDashboardAccess({
      url,
      data: { allowed_dashboard_tokens: userDomain.getAuthorizedDashboardTokens(dashboards) },
    })
  }

  function onDelete() {
    deleteUser()
      .then((response) => {
        toast.success('User successfully removed from the workspace.')
        mutate(ACCOUNTS_URL, (accountsResponse) => _mutateDeleteUserCallback(accountsResponse))
        deleteUserModal.close()

        saveUserActivityLog({
          action: ACTION_NAME.remove_from_workspace,
          target: `usr_${user.id}`,
          statusCode: response.status,
          requestBody: {},
        })

        return null
      })
      .catch((error) => {
        toast.error('Oops! Something went wrong. Please try again.')
        saveUserActivityLog({
          action: ACTION_NAME.remove_from_workspace,
          target: `usr_${user.id}`,
          statusCode: error.response.status,
          requestBody: {},
        })
      })

    function _mutateDeleteUserCallback(accountsResponse) {
      return {
        ...accountsResponse,
        results: R.reject((account) => account.id === user.id, accountsResponse.results),
        count: accountsResponse.count - 1,
      }
    }
  }

  function onTransferOwnership() {
    editUser({
      data: { role: 'owner' },
    })
      .then(() => {
        toast.success('Ownership transferred.')
        mutate(ACCOUNTS_URL)
        mutate('account/users/me/v2/')
        transferOwnershipModal.close()

        return null
      })
      .catch(() => {
        toast.error(
          'Failed to transfer ownership. Please contact Simplesat support for assistance.'
        )
      })
  }

  const userMenuItems = [
    user.status === 'invited' && {
      icon: 'far fa-paper-plane',
      content: 'Resend invitation email',
      onClick: () => {
        onResendInvitationEmail()
        setIsOptionsOpen(false)
      },
    },
    {
      icon: 'far fa-edit',
      content: 'Edit',
      onClick: () => {
        editUserModal.open()
        setIsOptionsOpen(false)
      },
    },
    userDomain.canConfigureTeamMemberMapping(user) && {
      icon: 'far fa-user-headset',
      content: 'Team member mapping',
      onClick: () => {
        teamMemberMappingModal.open()
        setIsOptionsOpen(false)
      },
    },
    userDomain.canTransferOwnerShip(currentUser) && {
      icon: 'far fa-user-crown',
      content: 'Make owner',
      onClick: () => {
        transferOwnershipModal.open()
        setIsOptionsOpen(false)
      },
    },
    {
      icon: 'far fa-sign-out-alt',
      className: '!text-negative',
      content: 'Remove from workspace',
      onClick: () => {
        deleteUserModal.open()
        setIsOptionsOpen(false)
      },
    },
  ]

  return (
    <React.Fragment>
      <StyledPopup
        className="p0 shadow-none"
        trigger={<Icon name="far fa-ellipsis-vertical" link />}
        position="bottom right"
        on="click"
        open={isOptionsOpen}
        onOpen={() => setIsOptionsOpen(true)}
        onClose={() => setIsOptionsOpen(false)}
        popperModifiers={[{ name: 'escapeWithReference', options: { escapeWithReference: true } }]}
      >
        <StyledMenu secondary vertical style={{ width: 'auto' }}>
          {userMenuItems.filter(isTruthy).map(({ icon, content, ...props }, index) => {
            return (
              <Menu.Item key={index} {...props}>
                <StyledIcon name={icon} />
                {content}
              </Menu.Item>
            )
          })}
        </StyledMenu>
      </StyledPopup>
      <editUserModal.Modal size="small">
        <EditUserModalContent
          user={user}
          close={editUserModal.close}
          onEdit={onEdit}
          loading={isEditUserLoading}
          defaultDisableSSOValue={user.disable_sso}
          defaultRoleValue={user.role}
          roleOptions={roleOptions}
          surveyAccessSelectableRoles={surveyAccessSelectableRoles}
          dashbardAccessesSelectableRoles={dashbardAccessesSelectableRoles}
        />
      </editUserModal.Modal>
      <teamMemberMappingModal.Modal size="small">
        <TeamMemberMappingModalContent
          close={teamMemberMappingModal.close}
          user={user}
          teamMemberMapping={user.team_member_mapping_emails}
          onEditTeamMemberMapping={onEditTeamMemberMapping}
          isSaveButtonLoading={isEditTeamMemberMappingLoading}
          teamMembers={teamMembers}
          isTeamMembersLoading={isTeamMembersLoading}
        />
      </teamMemberMappingModal.Modal>
      <transferOwnershipModal.Modal size="small">
        <TransferOwnershipModalContent
          close={transferOwnershipModal.close}
          onTransferOwnership={onTransferOwnership}
          newOwner={user}
          loading={isEditUserLoading}
        />
      </transferOwnershipModal.Modal>
      <deleteUserModal.Modal size="tiny">
        <DeleteUserModalContent
          close={deleteUserModal.close}
          onDelete={onDelete}
          loading={isDeleteUserLoading}
        />
      </deleteUserModal.Modal>
    </React.Fragment>
  )
}

const isTruthy = (a) => a

/** @type {typeof Popup} */
const StyledPopup = styled(Popup)`
  .ui.menu .item {
    font-weight: 400;
  }
`

/** @type {typeof Menu} */
const StyledMenu = styled(Menu)`
  &&& {
    margin: 0;

    .item:last-child {
      margin-bottom: 0;
    }
  }
`

/** @type {typeof Icon} */
const StyledIcon = styled(Icon)`
  &&&&&& {
    float: none;
    margin-left: 0;
    margin-right: 0.8125rem;
  }
`



// WEBPACK FOOTER //
// ./node_modules/eslint-loader??ref--0-0!./src/components/UserTableRowOptions/container.js